import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Calculator, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { mockData } from '../mock';

const ROICalculator = () => {
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [monthlyLeads, setMonthlyLeads] = useState('');
  const [showResults, setShowResults] = useState(false);

  const industryData = mockData.roiCalculator.industries.find(
    (ind) => ind.value === selectedIndustry
  );

  const calculateROI = () => {
    if (!selectedIndustry || !monthlyLeads || monthlyLeads <= 0) return null;

    const leads = parseInt(monthlyLeads);
    const { conversionIncrease, avgTicket } = industryData;

    // Estimativa conservadora: 30% de perda atual sem IA
    const currentLossRate = 0.3;
    const leadsLost = Math.round(leads * currentLossRate);
    
    // Após IA: conversão aumenta
    const newConversions = Math.round(leadsLost * 0.7); // 70% dos perdidos são recuperados
    const monthlyIncrease = newConversions * avgTicket;
    const yearlyIncrease = monthlyIncrease * 12;

    return {
      leadsLost,
      newConversions,
      monthlyIncrease,
      yearlyIncrease,
      roiMultiplier: conversionIncrease
    };
  };

  const results = calculateROI();

  const handleCalculate = () => {
    if (selectedIndustry && monthlyLeads && monthlyLeads > 0) {
      setShowResults(true);
    }
  };

  const handleWhatsApp = () => {
    const industryLabel = industryData?.label || 'seu negócio';
    const message = `Olá! Fiz a simulação na calculadora de ROI para ${industryLabel} e gostaria de saber mais sobre a implementação de IA.`;
    
    if (mockData.contact.whatsapp) {
      window.open(
        `https://wa.me/${mockData.contact.whatsapp}?text=${encodeURIComponent(message)}`,
        '_blank'
      );
    } else {
      alert('Em breve nosso WhatsApp estará disponível!');
    }
  };

  return (
    <section className="roi-calculator-section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", damping: 80, stiffness: 400 }}
          className="calculator-container"
        >
          <div className="calculator-header">
            <Calculator size={48} className="calculator-icon" />
            <h2 className="calculator-title">{mockData.roiCalculator.title}</h2>
            <p className="calculator-subtitle">{mockData.roiCalculator.subtitle}</p>
          </div>

          <Card className="calculator-card">
            <div className="calculator-inputs">
              <div className="input-group">
                <label className="input-label">Seu Ramo de Atuação</label>
                <Select onValueChange={setSelectedIndustry} value={selectedIndustry}>
                  <SelectTrigger className="select-trigger">
                    <SelectValue placeholder="Selecione seu ramo" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockData.roiCalculator.industries.map((industry) => (
                      <SelectItem key={industry.value} value={industry.value}>
                        {industry.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="input-group">
                <label className="input-label">Quantos leads você recebe por mês?</label>
                <input
                  type="number"
                  className="number-input"
                  placeholder="Ex: 50"
                  value={monthlyLeads}
                  onChange={(e) => {
                    setMonthlyLeads(e.target.value);
                    setShowResults(false);
                  }}
                  min="1"
                />
              </div>

              <Button
                onClick={handleCalculate}
                className="calculate-button"
                disabled={!selectedIndustry || !monthlyLeads || monthlyLeads <= 0}
              >
                <Calculator size={20} className="mr-2" />
                Calcular Projeção
              </Button>
            </div>

            {showResults && results && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.5 }}
                className="results-container"
              >
                <div className="results-header">
                  <TrendingUp size={32} className="results-icon" />
                  <h3 className="results-title">Sua Projeção Personalizada</h3>
                </div>

                <div className="results-grid">
                  <div className="result-card">
                    <div className="result-value">{results.leadsLost}</div>
                    <div className="result-label">Leads Perdidos/Mês</div>
                    <div className="result-sublabel">Por falta de resposta rápida</div>
                  </div>

                  <div className="result-card highlight">
                    <div className="result-value">+{results.newConversions}</div>
                    <div className="result-label">Conversões Extras/Mês</div>
                    <div className="result-sublabel">Com IA automatizada</div>
                  </div>

                  <div className="result-card highlight">
                    <div className="result-value">
                      +R$ {results.monthlyIncrease.toLocaleString('pt-BR')}
                    </div>
                    <div className="result-label">Receita Extra/Mês</div>
                    <div className="result-sublabel">Projeção conservadora</div>
                  </div>

                  <div className="result-card annual">
                    <div className="result-value">
                      R$ {results.yearlyIncrease.toLocaleString('pt-BR')}
                    </div>
                    <div className="result-label">Potencial Anual</div>
                    <div className="result-sublabel">Em 12 meses</div>
                  </div>
                </div>

                <div className="results-cta">
                  <p className="results-cta-text">
                    Quer descobrir como implementar isso no seu negócio?
                  </p>
                  <Button onClick={handleWhatsApp} className="whatsapp-button">
                    Falar com Especialista
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default ROICalculator;
